import React, { useMemo } from 'react';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import adminData from '../data/admin-data.json';

// ── Data helpers ───────────────────────────────────────────────────
const TIER_ORDER = ['free', 'season_pass', 'navigator', 'tactician'];
const TIER_LABELS = { free: 'Free', season_pass: 'Sailor', navigator: 'Navigator', tactician: 'Tactician' };
const TIER_PRICES = { free: '€0', season_pass: '~€10/season', navigator: '~€40/yr', tactician: '~€70/yr' };
const TIER_CSS = { free: 'free', season_pass: 'sailor', navigator: 'navigator', tactician: 'tactician' };

/** Flatten capabilities from admin data.json, expanding children. */
function flattenCapabilities() {
  const caps = adminData.capabilities;
  const rows = [];
  for (const tier of TIER_ORDER) {
    const items = caps[tier] || [];
    for (const item of items) {
      rows.push({ ...item, tier });
      if (item.children) {
        for (const child of item.children) {
          rows.push({ ...child, tier, isChild: true });
        }
      }
    }
  }
  return rows;
}

/** Count capabilities per build tier. */
function buildCounts(rows) {
  const counts = { internal: 0, beta: 0, public: 0 };
  for (const r of rows) {
    if (r.build.internal) counts.internal++;
    if (r.build.beta) counts.beta++;
    if (r.build.public) counts.public++;
  }
  return counts;
}

// ── Sub-components ─────────────────────────────────────────────────

function TierPill({ tier }) {
  return <span className={`gm-pill gm-pill-${TIER_CSS[tier]}`}>{TIER_LABELS[tier]}</span>;
}

function GatingBadge({ gating }) {
  if (gating === 'ui') return <span className="gm-gating gm-gating-ui">UI Gated</span>;
  if (gating === 'compile') return <span className="gm-gating gm-gating-compile">Compile</span>;
  return <span className="gm-gating gm-gating-none">None</span>;
}

function Check({ ok }) {
  return <td className={`gm-check ${ok ? 'gm-on' : 'gm-off'}`}>{ok ? '✓' : '✗'}</td>;
}

function CapabilityTable({ rows }) {
  // Pre-compute which rows need a tier divider (avoids mutable state in render)
  const dividerAt = new Set();
  let prevTier = null;
  rows.forEach((row, i) => {
    if (row.tier !== prevTier && !row.isChild) dividerAt.add(i);
    prevTier = row.tier;
  });

  return (
    <table className="gm-matrix">
      <thead>
        <tr>
          <th>Capability</th>
          <th>Tier</th>
          <th>Remote Flag</th>
          <th>Gating</th>
          <th className="gm-center">INT</th>
          <th className="gm-center">BETA</th>
          <th className="gm-center">PUB</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <React.Fragment key={row.name + i}>
            {dividerAt.has(i) && (
              <tr>
                <td colSpan="7" className={`gm-tier-divider gm-tier-divider-${TIER_CSS[row.tier]}`}>
                  {TIER_LABELS[row.tier]} {TIER_PRICES[row.tier] !== '€0' && <span className="gm-tier-price">{TIER_PRICES[row.tier]}</span>}
                </td>
              </tr>
            )}
            <tr>
              <td>{row.isChild ? <span className="gm-child-indent">└ </span> : null}<strong>{row.label}</strong></td>
              <td><TierPill tier={row.tier} /></td>
              <td>{row.flag ? <code className="gm-flag-code">{row.flag}</code> : <span className="gm-muted">—</span>}</td>
              <td><GatingBadge gating={row.gating} /></td>
              <Check ok={row.build.internal} />
              <Check ok={row.build.beta} />
              <Check ok={row.build.public} />
            </tr>
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

// ── Main page ──────────────────────────────────────────────────────

export default function GatingModel() {
  const rows = useMemo(flattenCapabilities, []);
  const counts = useMemo(() => buildCounts(rows), [rows]);

  return (
    <>
      <PortalHeader />
      <div className="page" style={{ maxWidth: 1060 }}>
        <PageHeader backTo="/admin" backLabel="Admin" title="Feature Switches & Gating Model" />

        {/* ─── Section 1: 3-Layer Flow ──────────────────────────── */}
        <section className="gm-section">
          <h2>3-Layer Gating</h2>
          <p className="gm-subtitle">Every capability passes through three gates before a user can access it.</p>

          <div className="gating-flow">
            <div className="gating-box l1">
              <strong>L1 Compile</strong>
              <span>Is code in the binary?</span>
            </div>
            <span className="gating-arrow">&rarr;</span>
            <div className="gating-box l2">
              <strong>L2 Remote Config</strong>
              <span>Kill switch ON?</span>
            </div>
            <span className="gating-arrow">&rarr;</span>
            <div className="gating-box l3">
              <strong>L3 Entitlement</strong>
              <span>Has user paid?</span>
            </div>
            <span className="gating-arrow">&rarr;</span>
            <div className="gating-box granted">
              <strong>Access Granted</strong>
              <span>Feature available</span>
            </div>
          </div>

          <div className="gm-note">
            L2 flags default to <strong>ON</strong> (fail-open) &mdash; they are kill switches, not feature flags.
            If L1 is false, code is tree-shaken and L2/L3 are never reached.
          </div>

          <table className="gm-scenario-table">
            <thead><tr><th>Scenario</th><th className="gm-center">L1</th><th className="gm-center">L2</th><th className="gm-center">L3</th></tr></thead>
            <tbody>
              <tr><td>Capability not started</td><td className="gm-check gm-off">✗</td><td className="gm-check gm-muted">&mdash;</td><td className="gm-check gm-muted">&mdash;</td></tr>
              <tr><td>Internal testing</td><td className="gm-check gm-on">✓</td><td className="gm-check gm-on">✓</td><td className="gm-check gm-bypass">debug</td></tr>
              <tr><td>Beta soft launch</td><td className="gm-check gm-on">✓</td><td className="gm-check gm-flip">✗ → ✓</td><td className="gm-check gm-muted">&mdash;</td></tr>
              <tr><td>Launched + paid</td><td className="gm-check gm-on">✓</td><td className="gm-check gm-on">✓</td><td className="gm-check gm-on">✓</td></tr>
              <tr><td>Launched + free tier</td><td className="gm-check gm-on">✓</td><td className="gm-check gm-on">✓</td><td className="gm-check gm-muted">skip</td></tr>
              <tr><td>Emergency kill</td><td className="gm-check gm-on">✓</td><td className="gm-check gm-off">✗</td><td className="gm-check gm-muted">&mdash;</td></tr>
            </tbody>
          </table>
        </section>

        {/* ─── Section 2: Pricing Tiers ─────────────────────────── */}
        <section className="gm-section">
          <h2>Pricing Tiers</h2>
          <p className="gm-subtitle">Two independent subscription axes: content (sailor) and features (navigator).</p>

          <div className="gm-note" style={{ marginBottom: '1rem' }}>
            <strong>Navigator does NOT grant content.</strong> A user can have features without content, or content without features. They are separate purchases.
          </div>

          <div className="gm-tier-bands">
            <div className="gm-band gm-band-free">
              <h4>Free</h4>
              <div className="gm-band-price">€0</div>
              <ul>
                {(adminData.capabilities.free || []).map(c => <li key={c.name}>{c.label}</li>)}
              </ul>
            </div>
            <div className="gm-band-arrow">&sube;</div>
            <div className="gm-band gm-band-sailor">
              <h4>Sailor</h4>
              <div className="gm-band-price">~€10/season</div>
              <ul>
                {(adminData.capabilities.season_pass || []).map(c => <li key={c.name}>{c.label}</li>)}
              </ul>
            </div>
            <div className="gm-band-arrow">&sube;</div>
            <div className="gm-band gm-band-navigator">
              <h4>Navigator</h4>
              <div className="gm-band-price">~€40/yr</div>
              <ul>
                {(adminData.capabilities.navigator || []).map(c => <li key={c.name}>{c.label}</li>)}
              </ul>
            </div>
            <div className="gm-band-arrow">&sube;</div>
            <div className="gm-band gm-band-tactician">
              <h4>Tactician</h4>
              <div className="gm-band-price">~€70/yr</div>
              <ul>
                {(adminData.capabilities.tactician || []).map(c => <li key={c.name}>{c.label}</li>)}
              </ul>
            </div>
          </div>

          <div className="gm-legend">
            <span><span className="gm-legend-dot" style={{ background: '#2e7d32' }} /> Free &mdash; always granted</span>
            <span><span className="gm-legend-dot" style={{ background: '#1565c0' }} /> Sailor &mdash; per-club entitlement (cc_*)</span>
            <span><span className="gm-legend-dot" style={{ background: '#e65100' }} /> Navigator &mdash; global entitlement</span>
            <span><span className="gm-legend-dot" style={{ background: '#b71c1c' }} /> Tactician &mdash; global entitlement</span>
          </div>
        </section>

        {/* ─── Section 3: Build Tier Matrix ─────────────────────── */}
        <section className="gm-section">
          <h2>Build Configs &amp; Capability Matrix</h2>
          <p className="gm-subtitle">What&rsquo;s compiled into each build tier.</p>

          <div className="gm-build-cards">
            <div className="gm-build-card gm-build-internal">
              <div className="gm-build-count">{counts.internal}</div>
              <div className="gm-build-label">Internal</div>
              <div className="gm-build-desc">All capabilities</div>
            </div>
            <div className="gm-build-card gm-build-beta">
              <div className="gm-build-count">{counts.beta}</div>
              <div className="gm-build-label">Beta</div>
              <div className="gm-build-desc">Shipped + near-ready</div>
            </div>
            <div className="gm-build-card gm-build-public">
              <div className="gm-build-count">{counts.public}</div>
              <div className="gm-build-label">Public</div>
              <div className="gm-build-desc">Store release</div>
            </div>
          </div>

          <CapabilityTable rows={rows} />
        </section>

        {/* ─── Section 4: Access Sources ────────────────────────── */}
        <section className="gm-section">
          <h2>L3 Access Sources</h2>
          <p className="gm-subtitle">How users can pass the entitlement gate without (or before) paying.</p>

          <div className="gm-access-grid">
            <div className="gm-access-card gm-access-rc">
              <h4>RevenueCat Entitlement</h4>
              <ul>
                <li><code>navigator</code> &mdash; global features</li>
                <li><code>tactician</code> &mdash; all capabilities</li>
                <li><code>cc_*</code> &mdash; per-club content</li>
                <li>Checked via <code>hasActiveEntitlement()</code></li>
              </ul>
            </div>
            <div className="gm-access-card gm-access-remote">
              <h4>Remote Tier Override</h4>
              <ul>
                <li>Firebase key: <code>tier_override</code></li>
                <li>Set to &ldquo;navigator&rdquo; &rarr; all nav features</li>
                <li>Works in release builds (TestFlight)</li>
                <li>Navigator override grants ALL tiers</li>
              </ul>
            </div>
            <div className="gm-access-card gm-access-beta">
              <h4>Beta Tester (Invite Code)</h4>
              <ul>
                <li>Firestore record from code redemption</li>
                <li>Tier stored in <code>BetaTesterModel</code></li>
                <li>Checked before RevenueCat</li>
                <li>Has active / expired state</li>
              </ul>
            </div>
            <div className="gm-access-card gm-access-debug">
              <h4>Debug Overrides</h4>
              <ul>
                <li><code>debugUseEntitlementOverrides</code></li>
                <li>Toggle individual entitlements</li>
                <li>Settings screen in debug builds only</li>
                <li><code>kDebugMode</code> gate &mdash; never in release</li>
              </ul>
            </div>
            <div className="gm-access-card gm-access-promo">
              <h4>Active Promos</h4>
              <ul>
                <li>JSON in Remote Config</li>
                <li>Time-windowed (start / end dates)</li>
                <li>Event-scoped (club/event IDs)</li>
                <li>e.g. DBSC Summer 2025 preview</li>
              </ul>
            </div>
          </div>

          <div className="gm-note">
            <strong>Priority:</strong> Debug overrides &rarr; Remote tier override &rarr; Beta tester &rarr; RevenueCat &rarr; Promo
          </div>
        </section>

        {/* ─── Section 5: Remote Config Flags ───────────────────── */}
        <section className="gm-section">
          <h2>Non-Capability Remote Config Flags</h2>

          <div className="gm-flag-grid">
            <div className="gm-flag"><code>tier_override</code> <span className="gm-flag-val gm-muted">&ldquo;&rdquo;</span></div>
            <div className="gm-flag"><code>enable_invite_codes</code> <span className="gm-flag-val gm-off">false</span></div>
            <div className="gm-flag"><code>send_debug_to_logrocket</code> <span className="gm-flag-val gm-off">false</span></div>
            <div className="gm-flag"><code>support_email</code> <span className="gm-flag-val gm-muted">support@racemarks.app</span></div>
            <div className="gm-flag"><code>support_phone</code> <span className="gm-flag-val gm-muted">+35315365358</span></div>
          </div>

          <h3 style={{ margin: '1.25rem 0 0.5rem' }}>Active Promos (JSON flags)</h3>
          <p className="gm-promo-desc">
            The app scans every Remote Config key matching <code>promo_*</code> or <code>promoN</code>.
            New campaigns can be added in the console — no app deploy required.
            JSON shape: <code>{`{"label","events":["clubId/eventId"],"start","end","tier":"sailor"}`}</code>
          </p>
          <div className="gm-promo-card">
            <strong>promo1 … promo4</strong>
            <code className="gm-promo-json">(empty by default — populate in console to activate)</code>
            <p className="gm-promo-desc">Generic slots for new campaigns</p>
          </div>
          <div className="gm-promo-card">
            <strong>promo_hyc_spring2026</strong>
            <code className="gm-promo-json">{`{"label":"Free until June","events":["ie-dublin-bay/2026-summer"],"start":"2026-04-01","end":"2026-10-31","tier":"sailor"}`}</code>
            <p className="gm-promo-desc">Grants sailor-tier access to DBSC Summer 2026 content (legacy key)</p>
          </div>
        </section>

        {/* ─── Section 6: Config Pipeline ───────────────────────── */}
        <section className="gm-section">
          <h2>Config Pipeline</h2>
          <p className="gm-subtitle">Source of truth and generated files.</p>

          <pre className="gm-pipeline">{`config/capabilities.yaml            ← EDIT THIS
    │
    ├──→ lib/core/features/capabilities.gen.dart     (Dart const bools)
    ├──→ ios/Capabilities.generated.xcconfig          (Xcode build settings)
    ├──→ ios/Runner/Capabilities.generated.swift      (Swift constants)
    └──→ firebase/remote_config_template.json         (Firebase Remote Config)

config/entitlements.yaml             ← products & pricing
    │
    ├──→ ios/Products.storekit                        (StoreKit testing)
    └──→ RevenueCat dashboard                         (manual sync!)

Build:  TIER=internal  tools/build_ios.sh
        TIER=beta      tools/build_ios.sh
        TIER=public    tools/build_ios.sh`}</pre>
        </section>

        {/* ─── Section 7: Key Files ─────────────────────────────── */}
        <section className="gm-section">
          <h2>Key Files</h2>
          <table className="gm-files-table">
            <thead><tr><th>File</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td><code>config/capabilities.yaml</code></td><td>Source of truth &mdash; edit this</td></tr>
              <tr><td><code>lib/core/features/capabilities.dart</code></td><td>Capability enum + PricingTier</td></tr>
              <tr><td><code>lib/core/features/capabilities.gen.dart</code></td><td>Generated compile-time bools</td></tr>
              <tr><td><code>lib/core/features/capability_access.dart</code></td><td>Imperative 3-layer checker</td></tr>
              <tr><td><code>lib/core/widgets/capability_gate.dart</code></td><td>Widget gating with per-layer fallbacks</td></tr>
              <tr><td><code>lib/core/providers/capability_provider.dart</code></td><td>Reactive UI provider + overrides</td></tr>
              <tr><td><code>lib/core/services/feature_flag_service.dart</code></td><td>Firebase Remote Config wrapper</td></tr>
              <tr><td><code>lib/core/services/revenuecat_service.dart</code></td><td>RevenueCat SDK wrapper</td></tr>
              <tr><td><code>config/entitlements.yaml</code></td><td>Products, entitlements, offerings</td></tr>
            </tbody>
          </table>
        </section>

      </div>
    </>
  );
}
